import { MapState } from 'src/app/interfaces/map.interface';
import { CustomMarker } from 'src/app/models/custom-marker.model';
import { MapMarkers } from 'src/app/models/map-markers.model';
import { AggregatedItemTypes } from './map.enum';
import MapSettings from './map.settings';

/**
 * @description Checks if the value is in range of max and min values.
 * @param { number } min
 * @param { number } max
 * @param { number } value
 * @returns { boolean }
 */
export function inRange(min: number, max: number, value: number): boolean {
  if (min > max) {
    const temp: number = min;
    min = max;
    max = temp;
  }

  return value >= min && value < max;
}

/**
 * @description Checks if the current and previous zoom levels are in range between
 *              city zoom level and max allowed zoom level.
 * @param { number } previous
 * @param { number } current
 * @returns { boolean }
 */
export function allInNotAggregationZoomRange(previous: number, current: number): boolean {
  return isInNotAggregationZoomRange(previous) && isInNotAggregationZoomRange(current);
}

/**
 * @description Checks if the passed zoom levels is in range between
 *              city zoom level and max allowed zoom level.
 * @param { number } zoom
 * @returns { boolean }
 */
export function isInNotAggregationZoomRange(zoom: number): boolean {
  return inRange(MapSettings.MAP_SEARCH_INDIVIDUAL_ZOOM_MIN_LEVEL, MapSettings.MAP_MAX_ZOOM_LEVEL + 1, zoom);
}

/**
 * @description Checks if the passed zoom levels is in range between
 *              min allowed zoom level and city zoom level.
 * @param { number } zoom
 * @returns { boolean }
 */
export function isInAggregationZoomRange(zoom: number): boolean {
  return inRange(MapSettings.MAP_MIN_ZOOM_LEVEL, MapSettings.MAP_SEARCH_INDIVIDUAL_ZOOM_MIN_LEVEL, zoom);
}

export function isTransitioningToIndividualsZoomLevel(currentZoom: number): boolean {
  return isInAggregationZoomRange(currentZoom) && !isInAggregationZoomRange(currentZoom + 1);
}

export function isInContinentCountryZoomRange(zoom: number): boolean {
  return zoom <= MapSettings.MAP_SEARCH_CONTINENT_ZOOM_MIN_LEVEL;
}

export function isInAggregationCountryZoomRange(zoom: number): boolean {
  return inRange(MapSettings.MAP_SEARCH_COUNTRY_ZOOM_MIN_LEVEL, MapSettings.MAP_SEARCH_REGION_ZOOM_MIN_LEVEL, zoom);
}

export function isInAggregationRegionZoomRange(zoom: number): boolean {
  return inRange(MapSettings.MAP_SEARCH_REGION_ZOOM_MIN_LEVEL, MapSettings.MAP_SEARCH_SUBREGION_ZOOM_MIN_LEVEL, zoom);
}

export function isInAggregationSubregionZoomRange(zoom: number): boolean {
  return inRange(
    MapSettings.MAP_SEARCH_SUBREGION_ZOOM_MIN_LEVEL,
    MapSettings.MAP_SEARCH_INDIVIDUAL_ZOOM_MIN_LEVEL,
    zoom,
  );
}

export function removeNotRelatedProperties(state: MapState): MapState {
  state.zoom = undefined;
  state.location = undefined;
  return state;
}

/**
 * @description Returns filtered map state without zoom and location props to divide map
 *              state and tab related props.
 * @param { MapState } state
 * @returns { MapState }
 */
export function getTabRelatedMapState(state: MapState): MapState {
  return removeNotRelatedProperties(structuredClone(state));
}

/**
 * @description Creates map markers object from incoming markers list.
 * @param { CustomMarker[] } markers
 * @param { number } totalMatchesCount
 * @returns { MapMarkers }
 */
export function getMapMarkers(markers: CustomMarker[], totalMatchesCount = 0): MapMarkers {
  return new MapMarkers(markers, markers, totalMatchesCount);
}

/**
 * @description Checks whether it has at least one aggregated item in the markers list or not.
 * @param { CustomMarker[] } markers
 * @returns { MapMarkers }
 */
export function hasAggregatedMarkers(markers: CustomMarker[]): boolean {
  return Boolean(markers.find((marker: CustomMarker) => marker.isAggregated));
}

/**
 * @description Checks whether it has at least one covid item in the markers list or not.
 * @param { CustomMarker[] } markers
 * @returns { MapMarkers }
 */
export function hasCovidMarkers(markers: CustomMarker[]): boolean {
  return Boolean(markers.find((marker: CustomMarker) => Boolean(marker.infoLabels)));
}

export function hasIndividualMarkers(markers: CustomMarker[]): boolean {
  const individualAggregationTypes: string[] = [
    AggregatedItemTypes.AGGREGATION_DOCTOR,
    AggregatedItemTypes.AGGREGATION_MED_STAFF,
    AggregatedItemTypes.AGGREGATION_PATIENT,
    AggregatedItemTypes.AGGREGATION_RESEARCHER,
  ];
  return markers.some((marker: CustomMarker) => individualAggregationTypes.includes(marker.kind));
}

/**
 * @description Calculates the decreasing step/coefficient of progression depend on zoom
 *              level for some another calculation.
 * @param { number } zoom
 * @returns { number }
 */
export function getCalculatedStep(zoom: number): number {
  return Math.pow(2, zoom - MapSettings.MAP_SEARCH_INDIVIDUAL_ZOOM_MIN_LEVEL);
}

/**
 * @description Returns the max allowed distance between two centers of states depend on
 *              zoom level.
 * @param { number } zoom
 * @returns { number }
 */
export function getMaxDistance(zoom: number): number {
  if (inRange(0, MapSettings.MAP_SEARCH_INDIVIDUAL_ZOOM_MIN_LEVEL + 1, zoom)) {
    return (
      MapSettings.DEFAULT_MAX_DISTANCE_BETWEEN_TWO_POINTS *
      Math.pow(2, MapSettings.MAP_SEARCH_INDIVIDUAL_ZOOM_MIN_LEVEL - zoom)
    );
  }

  return MapSettings.DEFAULT_MAX_DISTANCE_BETWEEN_TWO_POINTS / getCalculatedStep(zoom);
}

export function calculateOffsetDegree(currentZoom: number): number {
  const BASE_OFFSET_DEGREES = 0.01;
  const ZOOM_LEVEL_DIFFERENCE = 1.5;

  return Math.pow(ZOOM_LEVEL_DIFFERENCE, MapSettings.MAP_MAX_ZOOM_LEVEL - currentZoom) * BASE_OFFSET_DEGREES;
}
